/* eslint-disable array-bracket-newline */
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { useAccount } from 'wagmi';
import TonWeb from 'tonweb';
import TonConnect, { CHAIN } from '@tonconnect/sdk';
import { ICoin } from '../reducers/dex/tradingReducer';
import { actionAppendTransaction } from '../../../actions/transactions';
import { toIntermediateTransaction } from '../../../helpers/intermediateTransactions';
import { TON_CHAIN_ID } from '../../../helpers/customChains';
import { OnRefresh } from '../../../wallet/walletContext';
import {
  ITonSwapSimulateResponse,
  getTonTxParams,
} from '../helpers/tonTrading';
import { fetchTonBalance } from '../../../helpers/fetchTonBalance';

const MIN_TON_AMOUNT = 0.35;

enum swapTexts {
  swap = 'Swap',
  allowance = 'Asking allowance',
  allowanceMining = 'Mining allowance',
  txAsking = 'Asking transaction',
  txMining = 'Mining transaction',
}

interface ElrondTradeProps {
  base: ICoin;
  quote: ICoin;
  tokenRoute?: ITonSwapSimulateResponse;
  userAddress?: string;
  onNotify: (input: any) => void;
  onWalletRefresh: OnRefresh;
}

/**
 * Actual trade itself
 * @returns
 */
const useTonTrade = ({
  base,
  quote,
  tokenRoute,
  userAddress,
  onNotify,
  onWalletRefresh,
}: ElrondTradeProps) => {
  const dispatch = useDispatch();
  const [lock, setLock] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [swapText, setSwapText] = useState<string>(swapTexts.swap);
  const { connector } = useAccount();

  const resetState = () => {
    setLoading(false);
    setLock(false);
    setSwapText(swapTexts.swap);
    onWalletRefresh([base.address, quote.address]);
  };

  /**
   * Actual swap code
   */
  const onSwap = async () => {
    if (!userAddress || !tokenRoute) {
      return;
    }

    if (!connector || lock) {
      return;
    }

    const tonBalance = await fetchTonBalance({
      address: userAddress,
      token: '',
    });

    if (tonBalance.toNumber() < MIN_TON_AMOUNT) {
      onNotify({
        kind: 'warning',
        message: 'You need at least 0.35 TON to be able to swap',
      });
      return;
    }

    const provider = (await connector.getProvider()) as TonConnect;

    setLock(true);

    const amount = base.amount.times(10 ** base.decimals).toString();

    const txParams = await getTonTxParams(
      userAddress,
      base.address,
      quote.address,
      amount,
      tokenRoute.min_ask_units
    );

    provider
      .sendTransaction({
        validUntil: Date.now() + 1000000,
        network: CHAIN.MAINNET,
        messages: [
          {
            address: txParams.to.toString(),
            amount: txParams.gasAmount
              .add(TonWeb.utils.toNano('0.03'))
              .toString(),
            payload: TonWeb.utils.bytesToBase64(await txParams.payload.toBoc()),
          },
        ],
      })
      .then(async (result) => {
        const bocCell = TonWeb.boc.Cell.oneFromBoc(
          TonWeb.utils.base64ToBytes(result.boc)
        );
        const msgHash = TonWeb.utils.bytesToBase64(await bocCell.hash());
        dispatch(
          actionAppendTransaction(
            toIntermediateTransaction(msgHash, TON_CHAIN_ID, true)
          )
        );
        console.log({ result });

        onNotify({ message: 'Transaction sent successfully' });
      })
      .catch((e) => {
        onNotify({
          kind: 'warning',
          message: `Something went wrong: ${e}`,
        });
        console.log({ e });
      })
      .finally(() => {
        resetState();
      });
  };

  return {
    loading,
    onSwap,
    swapText,
  };
};

export default useTonTrade;
